// SideRobot — fixed right-edge feature mascot rotated so the robot lies on
// the page edge with its head pointing into the viewport (leftward). The
// container tilts slightly with the cursor's vertical position so the mascot
// appears to look wherever the cursor moves. Desktop only, non-interactive,
// respects prefers-reduced-motion.
import React, { useEffect, useRef, useState } from "react";
import SplineHero from "@/components/SplineHero";

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

const SideRobot = () => {
  const wrapRef = useRef(null);
  const tiltRef = useRef(0);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const wide = window.matchMedia("(min-width: 1024px)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setEnabled(finePointer && wide && !reduced);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let rafId = 0;
    let targetTilt = 0;

    const apply = () => {
      if (!wrapRef.current) return;
      wrapRef.current.style.transform =
        `translate(42%, -50%) rotate(${-90 + tiltRef.current}deg)`;
    };
    apply();

    const onMove = (e) => {
      const vy = e.clientY / window.innerHeight; // 0 top → 1 bottom
      targetTilt = clamp((vy - 0.5) * 24, -12, 12);
    };

    const tick = () => {
      tiltRef.current += (targetTilt - tiltRef.current) * 0.08;
      apply();
      rafId = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    rafId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("mousemove", onMove);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div
      ref={wrapRef}
      data-testid="side-robot"
      aria-hidden="true"
      // Spline scene has native look-at-cursor. Container must receive pointer
      // events for that to fire; kept off the left content column since the
      // mascot lives on the right edge and does not overlap CTAs.
      className="pointer-events-auto fixed right-0 top-1/2 z-20 hidden xl:block"
      style={{
        width: "min(360px, 36vh)",
        height: "min(360px, 36vh)",
        transform: "translate(42%, -50%) rotate(-90deg)",
        transformOrigin: "center center",
        willChange: "transform",
      }}
    >
      <SplineHero className="w-full h-full" />
    </div>
  );
};

export default SideRobot;
